/**
 * Adaptive synthwave score, generated live. A look-ahead scheduler places
 * every note on the AudioContext clock, so timing stays sample-accurate even
 * when the render loop hitches.
 *
 * Arrangement:
 *  - menu / lobby: slow pad chords, sub bass and a delayed arpeggio (lobby adds
 *    a light pulse) — calm, but clearly the same theme as the race.
 *  - countdown: the pad holds while a filtered riser builds into lights-out.
 *  - race: four-on-the-floor, rolling bass and hats; snare, 16th hats and the
 *    arpeggio join as intensity (speed, nitro, pack proximity) rises; the final
 *    lap modulates up a whole tone and brings in the lead.
 *  - finish: drums stop and a resolving chord rings out into the results pad.
 *
 * Synth layers are side-chained to the kick so the mix breathes like a
 * produced track instead of a stack of beeps.
 */
export type ScoreMode = 'off' | 'menu' | 'lobby' | 'countdown' | 'race' | 'finish';

interface Chord {
  root: number;
  third: number;
  fifth: number;
}

// i – VI – III – VII in natural minor.
const PROGRESSION: readonly Chord[] = [
  { root: 0, third: 3, fifth: 7 },
  { root: 8, third: 4, fifth: 7 },
  { root: 3, third: 4, fifth: 7 },
  { root: 10, third: 4, fifth: 7 },
];

// Lead motif for the final lap, one bar per chord (semitones above the key root).
const LEAD: readonly (readonly (number | null)[])[] = [
  [12, null, null, 15, null, null, 19, null, 17, null, 15, null, 12, null, null, null],
  [15, null, null, 12, null, null, 8, null, 10, null, 12, null, 15, null, null, null],
  [19, null, null, 22, null, null, 19, null, 17, null, 15, null, 19, null, null, null],
  [22, null, null, 19, null, null, 17, null, 14, null, 17, null, 19, null, 22, null],
];

const ARP_ORDER = [0, 1, 2, 3, 2, 1, 0, 1] as const;
const KEY_ROOT_MIDI = 45; // A2

export class AdaptiveScore {
  private readonly output: GainNode;
  private readonly tone: BiquadFilterNode;
  private readonly synthBus: GainNode;
  private readonly pump: GainNode;
  private readonly drumBus: GainNode;
  private readonly delayInput: GainNode;
  private readonly reverbSend: GainNode;
  private readonly drums: Record<'kick' | 'snare' | 'clap' | 'hat' | 'open' | 'crash' | 'noise', AudioBuffer>;
  private mode: ScoreMode = 'off';
  private targetIntensity = 0;
  private intensity = 0;
  private finalLap = false;
  private boost = false;
  private keyOffset = 0;
  private step = 0;
  private nextStepTime = 0;
  private timer: number | null = null;
  private riser: { source: AudioBufferSourceNode; gain: GainNode } | null = null;
  private disposed = false;

  constructor(private readonly context: AudioContext, destination: AudioNode, reverb: AudioNode) {
    this.output = context.createGain();
    this.output.gain.value = 0;
    this.tone = context.createBiquadFilter();
    this.tone.type = 'lowpass';
    this.tone.frequency.value = 2_400;
    this.tone.Q.value = 0.6;
    this.tone.connect(this.output).connect(destination);

    this.drumBus = context.createGain();
    this.drumBus.gain.value = 0.45;
    this.drumBus.connect(this.tone);
    this.synthBus = context.createGain();
    this.synthBus.gain.value = 0.4;
    this.pump = context.createGain();
    this.synthBus.connect(this.pump).connect(this.tone);

    // Ping-pong delay at a dotted eighth gives the arp and lead their width.
    this.delayInput = context.createGain();
    this.delayInput.gain.value = 0.42;
    const left = context.createDelay(2);
    const right = context.createDelay(2);
    left.delayTime.value = 0.36;
    right.delayTime.value = 0.36;
    const feedback = context.createGain();
    feedback.gain.value = 0.38;
    const damp = context.createBiquadFilter();
    damp.type = 'lowpass';
    damp.frequency.value = 3_200;
    const panLeft = context.createStereoPanner();
    panLeft.pan.value = -0.7;
    const panRight = context.createStereoPanner();
    panRight.pan.value = 0.7;
    this.delayInput.connect(left);
    left.connect(panLeft).connect(this.pump);
    left.connect(right);
    right.connect(panRight).connect(this.pump);
    right.connect(damp).connect(feedback).connect(left);
    this.delayDelays = [left, right];

    this.reverbSend = context.createGain();
    this.reverbSend.gain.value = 0.35;
    this.reverbSend.connect(reverb);

    this.drums = createDrumKit(context);
  }

  private readonly delayDelays: DelayNode[];

  get currentMode(): ScoreMode {
    return this.mode;
  }

  /** Picks a key per track so every venue has its own colour. */
  setKeyFromSeed(seed: string): void {
    let hash = 0;
    for (let index = 0; index < seed.length; index += 1) hash = (hash * 31 + seed.charCodeAt(index)) | 0;
    const offsets = [0, -3, 2, -2, 3, -5, 1];
    this.keyOffset = offsets[Math.abs(hash) % offsets.length]!;
  }

  setMode(mode: ScoreMode): void {
    if (this.disposed || mode === this.mode) return;
    const previous = this.mode;
    this.mode = mode;
    const now = this.context.currentTime;
    if (mode === 'off') {
      this.output.gain.cancelScheduledValues(now);
      this.output.gain.setTargetAtTime(0, now, 0.4);
      this.stopRiser(now);
      return;
    }
    this.ensureTimer();
    if (previous === 'off') {
      this.step = 0;
      this.nextStepTime = now + 0.08;
    }
    if (mode === 'countdown') this.startRiser(now);
    else this.stopRiser(now);
    if (mode === 'race' && (previous === 'countdown' || previous === 'lobby' || previous === 'menu' || previous === 'off')) {
      // Lights out: land the downbeat right now with a crash.
      this.step = 0;
      this.nextStepTime = now + 0.02;
      this.finalLap = false;
      this.playDrum('crash', now + 0.02, 0.55);
    }
    if (mode === 'finish') this.playFinishChord(now + 0.05);
    if (mode === 'menu' || mode === 'lobby') this.finalLap = false;
  }

  /** 0..1, how hard the race currently feels. */
  setIntensity(value: number, boost = false): void {
    this.targetIntensity = Math.max(0, Math.min(1, value));
    this.boost = boost;
  }

  setFinalLap(finalLap: boolean): void {
    if (finalLap === this.finalLap) return;
    this.finalLap = finalLap;
    if (finalLap && this.mode === 'race') this.playDrum('crash', this.context.currentTime + 0.02, 0.5);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.timer !== null) window.clearInterval(this.timer);
    this.timer = null;
    try {
      this.output.disconnect();
      this.reverbSend.disconnect();
    } catch {
      // Context may already be closed.
    }
  }

  private ensureTimer(): void {
    if (this.timer !== null || typeof window === 'undefined') return;
    this.timer = window.setInterval(this.schedule, 25);
  }

  private readonly schedule = (): void => {
    if (this.disposed) return;
    const context = this.context;
    if (context.state !== 'running') return;
    const now = context.currentTime;
    this.intensity += (this.targetIntensity - this.intensity) * 0.06;
    this.updateMix(now);
    if (this.mode === 'off') {
      if (now > this.nextStepTime + 2 && this.timer !== null) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
      return;
    }
    const stepLength = 60 / this.tempo() / 4;
    if (this.nextStepTime < now - stepLength * 4) this.nextStepTime = now + 0.02;
    let guard = 0;
    while (this.nextStepTime < now + 0.14 && guard < 16) {
      this.scheduleStep(this.step, this.nextStepTime, stepLength);
      this.step = (this.step + 1) % 256;
      this.nextStepTime += stepLength;
      guard += 1;
    }
  };

  private tempo(): number {
    if (this.mode === 'race') return this.finalLap ? 132 : 124;
    if (this.mode === 'countdown') return 124;
    if (this.mode === 'finish') return 96;
    return this.mode === 'lobby' ? 104 : 96;
  }

  private updateMix(now: number): void {
    const intensity = this.intensity;
    let level: number;
    let cutoff: number;
    switch (this.mode) {
      case 'menu':
        level = 0.8;
        cutoff = 2_600;
        break;
      case 'lobby':
        level = 0.8;
        cutoff = 3_400;
        break;
      case 'countdown':
        level = 0.6;
        cutoff = 1_400;
        break;
      case 'race':
        // The engine is the hero in a race; the score sits ~5 dB underneath it.
        level = 0.13 + intensity * 0.06;
        cutoff = 1_800 + intensity * intensity * 12_000 + (this.boost ? 4_000 : 0) + (this.finalLap ? 3_000 : 0);
        break;
      case 'finish':
        level = 0.75;
        cutoff = 4_800;
        break;
      default:
        level = 0;
        cutoff = 2_000;
    }
    this.output.gain.setTargetAtTime(level, now, 0.6);
    this.tone.frequency.setTargetAtTime(Math.min(18_000, cutoff), now, 0.35);
    const delayTime = (60 / this.tempo()) * 0.75;
    for (const delay of this.delayDelays) delay.delayTime.setTargetAtTime(delayTime, now, 0.2);
  }

  private scheduleStep(step: number, time: number, stepLength: number): void {
    const mode = this.mode;
    const barStep = step % 16;
    const bar = Math.floor(step / 16);
    const chordIndex = mode === 'race' || mode === 'countdown'
      ? bar % 4
      : Math.floor(bar / 2) % 4;
    const chord = PROGRESSION[chordIndex]!;
    const key = KEY_ROOT_MIDI + this.keyOffset + (this.finalLap && mode === 'race' ? 2 : 0);
    const chordChange = mode === 'race' || mode === 'countdown' ? barStep === 0 : barStep === 0 && bar % 2 === 0;
    const intensity = this.intensity;

    if (mode === 'finish') {
      if (barStep === 0 && bar % 2 === 0 && step > 0) this.playPad(key, chord, time, stepLength * 32, 0.6);
      if (barStep % 4 === 2) this.playArp(key, chord, step, time, stepLength, 0.35);
      return;
    }

    if (chordChange) {
      const span = mode === 'race' || mode === 'countdown' ? stepLength * 16 : stepLength * 32;
      this.playPad(key, chord, time, span, mode === 'race' ? 0.55 + (1 - intensity) * 0.25 : 1);
    }

    if (mode === 'menu' || mode === 'lobby') {
      if (barStep === 0) this.playBass(key + chord.root, time, stepLength * (bar % 2 === 0 ? 14 : 12), 0.75, 380);
      if (barStep % 2 === 0) this.playArp(key, chord, step / 2, time, stepLength * 1.6, mode === 'lobby' ? 0.55 : 0.42);
      if (mode === 'lobby') {
        if (barStep % 4 === 0) this.playDrum('kick', time, 0.42, true);
        if (barStep % 4 === 2) this.playDrum('hat', time, 0.18);
      } else if (bar % 8 >= 4 && barStep % 8 === 4) {
        this.playDrum('hat', time, 0.12);
      }
      return;
    }

    if (mode === 'countdown') {
      if (barStep % 4 === 0) this.playBass(key + chord.root, time, stepLength * 1.4, 0.55, 260);
      return;
    }

    // Race.
    if (barStep % 4 === 0) this.playDrum('kick', time, 0.9, true);
    if (intensity > 0.32 && (barStep === 4 || barStep === 12)) {
      this.playDrum('snare', time, 0.62);
      if (intensity > 0.6 || this.finalLap) this.playDrum('clap', time + 0.004, 0.4);
    }
    if (barStep % 4 === 2) this.playDrum(this.finalLap ? 'open' : 'hat', time, 0.32);
    else if (intensity > 0.5 && barStep % 2 === 1) this.playDrum('hat', time, 0.14 + intensity * 0.08);
    if (barStep === 15 && bar % 4 === 3 && intensity > 0.45) this.playDrum('snare', time, 0.35);

    const bassNote = key + chord.root + (barStep % 8 === 6 ? 12 : 0);
    if (barStep % 2 === 1 || barStep % 4 === 2) this.playBass(bassNote, time, stepLength * 0.85, 0.85, 900 + intensity * 900);
    else if (barStep % 4 === 0) this.playBass(key + chord.root, time, stepLength * 0.7, 0.55, 600);

    if (intensity > 0.58 || this.boost || this.finalLap) this.playArp(key, chord, step, time, stepLength * 0.9, 0.42 + intensity * 0.18);
    if (this.finalLap) {
      const note = LEAD[chordIndex]![barStep];
      if (note !== null && note !== undefined) this.playLead(key + 12 + note, time, stepLength * 2.6);
    }
  }

  private playDrum(kind: Exclude<keyof AdaptiveScore['drums'], 'noise'>, time: number, gain: number, sidechain = false): void {
    const source = this.context.createBufferSource();
    source.buffer = this.drums[kind];
    const level = this.context.createGain();
    level.gain.value = gain;
    source.connect(level).connect(this.drumBus);
    if (kind === 'crash' || kind === 'open') level.connect(this.reverbSend);
    source.start(time);
    source.onended = () => level.disconnect();
    if (sidechain) {
      const pump = this.pump.gain;
      pump.cancelScheduledValues(time);
      pump.setValueAtTime(0.38, time);
      pump.setTargetAtTime(1, time + 0.02, 0.07);
    }
  }

  private playBass(midi: number, time: number, duration: number, gain: number, cutoff: number): void {
    const context = this.context;
    const frequency = midiToFrequency(midi);
    const saw = context.createOscillator();
    saw.type = 'sawtooth';
    saw.frequency.value = frequency;
    const sub = context.createOscillator();
    sub.type = 'sine';
    sub.frequency.value = frequency / 2;
    const filter = context.createBiquadFilter();
    filter.type = 'lowpass';
    filter.Q.value = 4;
    filter.frequency.setValueAtTime(cutoff * 2.2, time);
    filter.frequency.exponentialRampToValueAtTime(Math.max(120, cutoff * 0.35), time + Math.min(duration, 0.22));
    const envelope = context.createGain();
    envelope.gain.setValueAtTime(0.0001, time);
    envelope.gain.linearRampToValueAtTime(0.16 * gain, time + 0.006);
    envelope.gain.setTargetAtTime(0.0001, time + duration * 0.7, duration * 0.18 + 0.01);
    const subGain = context.createGain();
    subGain.gain.value = 0.9;
    saw.connect(filter).connect(envelope);
    sub.connect(subGain).connect(envelope);
    envelope.connect(this.synthBus);
    const end = time + duration + 0.25;
    saw.start(time);
    sub.start(time);
    saw.stop(end);
    sub.stop(end);
    sub.onended = () => envelope.disconnect();
  }

  private playArp(key: number, chord: Chord, step: number, time: number, duration: number, gain: number): void {
    const context = this.context;
    const notes = [chord.root, chord.root + chord.third, chord.root + chord.fifth, chord.root + 12];
    const index = ARP_ORDER[Math.floor(step) % ARP_ORDER.length]!;
    const midi = key + 24 + notes[index]!;
    const oscillator = context.createOscillator();
    oscillator.type = 'square';
    oscillator.frequency.value = midiToFrequency(midi);
    const filter = context.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(4_200, time);
    filter.frequency.exponentialRampToValueAtTime(900, time + Math.max(0.05, duration));
    const envelope = context.createGain();
    envelope.gain.setValueAtTime(0.0001, time);
    envelope.gain.linearRampToValueAtTime(0.045 * gain, time + 0.004);
    envelope.gain.exponentialRampToValueAtTime(0.0001, time + Math.max(0.06, duration));
    const pan = context.createStereoPanner();
    pan.pan.value = index % 2 === 0 ? -0.25 : 0.25;
    oscillator.connect(filter).connect(envelope).connect(pan);
    pan.connect(this.synthBus);
    pan.connect(this.delayInput);
    oscillator.start(time);
    oscillator.stop(time + duration + 0.05);
    oscillator.onended = () => pan.disconnect();
  }

  private playPad(key: number, chord: Chord, time: number, duration: number, gain: number): void {
    const context = this.context;
    const notes = [chord.root + 12, chord.root + chord.third + 12, chord.root + chord.fifth + 12, chord.root + 24];
    const filter = context.createBiquadFilter();
    filter.type = 'lowpass';
    filter.Q.value = 0.8;
    filter.frequency.setValueAtTime(700, time);
    filter.frequency.linearRampToValueAtTime(1_900, time + duration * 0.5);
    filter.frequency.linearRampToValueAtTime(1_100, time + duration);
    const envelope = context.createGain();
    const peak = 0.03 * gain;
    envelope.gain.setValueAtTime(0.0001, time);
    envelope.gain.linearRampToValueAtTime(peak, time + Math.min(0.9, duration * 0.25));
    envelope.gain.setValueAtTime(peak, time + duration * 0.8);
    envelope.gain.linearRampToValueAtTime(0.0001, time + duration * 1.15);
    filter.connect(envelope);
    envelope.connect(this.synthBus);
    envelope.connect(this.reverbSend);
    const end = time + duration * 1.2;
    const oscillators: OscillatorNode[] = [];
    notes.forEach((note, index) => {
      for (const detune of [-9, 9]) {
        const oscillator = context.createOscillator();
        oscillator.type = 'sawtooth';
        oscillator.frequency.value = midiToFrequency(key + note);
        oscillator.detune.value = detune + (index - 1.5) * 2;
        const pan = context.createStereoPanner();
        pan.pan.value = detune < 0 ? -0.55 : 0.55;
        oscillator.connect(pan).connect(filter);
        oscillator.start(time);
        oscillator.stop(end);
        oscillators.push(oscillator);
      }
    });
    oscillators[0]!.onended = () => envelope.disconnect();
  }

  private playLead(midi: number, time: number, duration: number): void {
    const context = this.context;
    const frequency = midiToFrequency(midi);
    const envelope = context.createGain();
    envelope.gain.setValueAtTime(0.0001, time);
    envelope.gain.linearRampToValueAtTime(0.05, time + 0.015);
    envelope.gain.setTargetAtTime(0.032, time + 0.04, 0.12);
    envelope.gain.setTargetAtTime(0.0001, time + duration, 0.09);
    const filter = context.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 3_600;
    filter.Q.value = 1.4;
    const vibrato = context.createOscillator();
    vibrato.frequency.value = 5.5;
    const vibratoDepth = context.createGain();
    vibratoDepth.gain.setValueAtTime(0, time);
    vibratoDepth.gain.linearRampToValueAtTime(frequency * 0.006, time + 0.25);
    vibrato.connect(vibratoDepth);
    const end = time + duration + 0.5;
    for (const detune of [-6, 6]) {
      const oscillator = context.createOscillator();
      oscillator.type = 'sawtooth';
      oscillator.frequency.value = frequency;
      oscillator.detune.value = detune;
      vibratoDepth.connect(oscillator.frequency);
      oscillator.connect(filter);
      oscillator.start(time);
      oscillator.stop(end);
    }
    vibrato.start(time);
    vibrato.stop(end);
    vibrato.onended = () => envelope.disconnect();
    filter.connect(envelope);
    envelope.connect(this.synthBus);
    envelope.connect(this.delayInput);
    envelope.connect(this.reverbSend);
  }

  private playFinishChord(time: number): void {
    const key = KEY_ROOT_MIDI + this.keyOffset + (this.finalLap ? 2 : 0);
    // Picardy third: resolve the minor theme on a bright major tonic.
    this.playPad(key, { root: 0, third: 4, fifth: 7 }, time, 4.5, 1.6);
    this.playDrum('crash', time, 0.6);
    this.playBass(key, time, 2.4, 1, 500);
  }

  private startRiser(now: number): void {
    if (this.riser) return;
    const context = this.context;
    const source = context.createBufferSource();
    source.buffer = this.drums.noise;
    source.loop = true;
    const filter = context.createBiquadFilter();
    filter.type = 'bandpass';
    filter.Q.value = 3;
    filter.frequency.setValueAtTime(300, now);
    filter.frequency.exponentialRampToValueAtTime(6_000, now + 3.2);
    const gain = context.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.22, now + 3);
    source.connect(filter).connect(gain).connect(this.tone);
    source.start(now);
    this.riser = { source, gain };
  }

  private stopRiser(now: number): void {
    const riser = this.riser;
    if (!riser) return;
    this.riser = null;
    riser.gain.gain.cancelScheduledValues(now);
    riser.gain.gain.setTargetAtTime(0.0001, now, 0.05);
    riser.source.stop(now + 0.4);
    riser.source.onended = () => riser.gain.disconnect();
  }
}

export function midiToFrequency(midi: number): number {
  return 440 * 2 ** ((midi - 69) / 12);
}

/** Drum one-shots synthesized straight into buffers, once per context. */
function createDrumKit(context: BaseAudioContext): AdaptiveScore['drums'] {
  const rate = context.sampleRate;
  let seed = 0x1234567;
  const random = () => {
    seed ^= seed << 13;
    seed ^= seed >>> 17;
    seed ^= seed << 5;
    return ((seed >>> 0) / 0xffffffff) * 2 - 1;
  };
  const make = (seconds: number, channels: number, fill: (t: number, channel: number) => number) => {
    const length = Math.max(1, Math.floor(rate * seconds));
    const buffer = context.createBuffer(channels, length, rate);
    for (let channel = 0; channel < channels; channel += 1) {
      const data = buffer.getChannelData(channel);
      for (let index = 0; index < length; index += 1) data[index] = fill(index / rate, channel);
    }
    return buffer;
  };

  let kickPhase = 0;
  const kick = make(0.5, 1, (t) => {
    const frequency = 46 + 120 * Math.exp(-t / 0.035);
    kickPhase += (2 * Math.PI * frequency) / rate;
    const body = Math.sin(kickPhase) * Math.exp(-t / 0.2);
    const click = t < 0.004 ? random() * (1 - t / 0.004) * 0.5 : 0;
    return Math.tanh((body + click) * 1.6) * 0.95;
  });

  let snareHp = 0;
  let snarePrev = 0;
  const snare = make(0.32, 1, (t) => {
    const white = random();
    snareHp = 0.82 * (snareHp + white - snarePrev);
    snarePrev = white;
    const noise = snareHp * Math.exp(-t / 0.075) * 0.7;
    const body = Math.sin(2 * Math.PI * 185 * t) * Math.exp(-t / 0.045) * 0.55;
    return noise + body;
  });

  let clapLow = 0;
  const clap = make(0.4, 1, (t) => {
    const white = random();
    clapLow += (white - clapLow) * 0.35;
    const band = white - clapLow;
    const bursts = [0, 0.011, 0.023].reduce((sum, start) => (
      t >= start ? sum + Math.exp(-(t - start) / 0.006) : sum
    ), 0);
    const tail = t > 0.023 ? Math.exp(-(t - 0.023) / 0.11) * 0.6 : 0;
    return band * Math.min(1.2, bursts + tail) * 0.8;
  });

  const hats = (decay: number, seconds: number) => {
    let previous = 0;
    let high = 0;
    return make(seconds, 2, (t, channel) => {
      const white = random();
      high = 0.6 * (high + white - previous);
      previous = white;
      // Metallic shimmer from a few square partials.
      const metal = [3_120, 4_480, 5_920, 7_350].reduce((sum, frequency, index) => (
        sum + Math.sign(Math.sin(2 * Math.PI * frequency * (1 + channel * 0.002) * t + index)) * 0.08
      ), 0);
      return (high * 0.7 + metal) * Math.exp(-t / decay) * 0.55;
    });
  };
  const hat = hats(0.018, 0.08);
  const open = hats(0.12, 0.45);

  let crashPrev = 0;
  let crashHigh = 0;
  const crash = make(2.2, 2, (t) => {
    const white = random();
    crashHigh = 0.7 * (crashHigh + white - crashPrev);
    crashPrev = white;
    return crashHigh * Math.exp(-t / 0.75) * (t < 0.002 ? t / 0.002 : 1) * 0.6;
  });

  const noise = make(2, 2, () => random() * 0.5);

  return { kick, snare, clap, hat, open, crash, noise };
}
